import { useState } from 'react'
import { LeaveOrganization } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { MembersRoster } from '@/organizations/views/MembersRoster'
import { useMembersRosterData } from '@/organizations/hooks/useMembersRosterData'
import InviteMemberDialog from '@/organizations/views/InviteMemberDialog'
import LeaveOrganizationModalContainer from '@/organizations/views/LeaveOrganizationModalContainer'

/**
 * Members section — the org roster only (billing lives in its own admin-only BillingPanel). Serves
 * both admins (row action menus, invite, logo editing) and members (read-only directory); every
 * difference is derived from the user's role in useMembersRosterData. Renders the shared
 * MembersRoster (its own org-name header), so it does not use SettingsPanelShell's title. Billing
 * props are intentionally not passed, so MembersRoster hides the billing stats and Manage
 * Subscription; the standalone sidebar MemberRosterModal still passes them for its combined view.
 */
export function MembersPanel() {
  const data = useMembersRosterData()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [leaveOpen, setLeaveOpen] = useState(false)

  // Safety net: the nav only surfaces this section for org accounts, but guard anyway.
  if (!data.isOrgAccount) return null

  const isAdmin = data.isOrgAdmin

  // Fills the FE-827 shell's h-full inner wrapper so MembersRoster gets a real height to resolve
  // its own internal flex scroll chain against (org header + search fixed, rows scroll independently).
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <MembersRoster
        orgName={data.orgName}
        orgLogoUrl={data.orgLogoUrl}
        currentUserId={data.currentUserId}
        members={data.members}
        onInviteMembers={isAdmin ? () => setInviteOpen(true) : undefined}
        onLogoFileSelected={isAdmin ? data.uploadLogoFile : undefined}
        onLogoDelete={isAdmin ? data.deleteLogo : undefined}
        isLogoBusy={data.isLogoBusy}
        showMemberMenu={isAdmin}
        onMemberAction={data.refetch}
        showInviteMenu={isAdmin}
        onInviteAction={data.refetch}
        isError={data.isError}
      />

      {/* The org payer cannot leave; everyone else gets the self-removal affordance (matches the roster modal). */}
      {!data.isOrgPayer && (
        <div className="flex justify-end">
          <Button variant="plain" color="destructive" leadingIcon={<LeaveOrganization className="size-4" aria-hidden />} onClick={() => setLeaveOpen(true)}>
            Leave Organization
          </Button>
        </div>
      )}

      {isAdmin && <InviteMemberDialog stacked visible={inviteOpen} onClose={() => setInviteOpen(false)} onInviteSuccess={() => data.refetch()} />}
      {leaveOpen && <LeaveOrganizationModalContainer visible organizationName={data.orgName} onClose={() => setLeaveOpen(false)} stacked />}
    </div>
  )
}
